import {
  Injectable,
  inject,
  EnvironmentInjector,
  PLATFORM_ID,
  computed,
  signal,
  effect,
} from '@angular/core';

import { Auth, getAuth, GoogleAuthProvider, OAuthProvider, onIdTokenChanged, signInWithPopup, User, UserCredential } from '@angular/fire/auth';


import { isPlatformBrowser } from '@angular/common';
import { FirebaseError } from '@firebase/util';
import { PopupService } from '@shared/services/popup-service';
import { TenantProvider } from '@shared/core/initializer/models/tenant.model';
import { AuthUser, BroadcastPayload, UserClaims } from '../models/login.model';
import { Router } from '@angular/router';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private router=inject(Router);

  // private tenantService = inject(TenantService);
  // private firebaseAuthFacade=inject(FirebaseAuthFacade);

  private injector = inject(EnvironmentInjector);

  private popupService = inject(PopupService);

  private platformId = inject(PLATFORM_ID);

  firebaseTenantId = "";

    private channel    = new BroadcastChannel('mfe-auth');
        private popup=inject(PopupService);




  currentUser = signal<AuthUser | null>(null);
  isReady     = signal<boolean>(false);

  isLoggedIn  = computed(() => !!this.currentUser());
  idToken     = computed(() => this.currentUser()?.idToken ?? null);
  claims      = computed(() => this.currentUser()?.claims ?? {} as UserClaims);
  userRoles   = computed(() => this.currentUser()?.claims?.role ?? '');
  firstName   = computed(() => this.currentUser()?.firstName ?? '');
  lastName    = computed(() => this.currentUser()?.lastName ?? '');
  fullName    = computed(() => this.currentUser()?.fullName ?? '');
  photoURL    = computed(() => this.currentUser()?.photoURL ?? null);
  email       = computed(() => this.currentUser()?.email ?? null);
  orgId       = computed(() => this.currentUser()?.claims?.orgId ?? null);
  tenantId       = computed(() => this.currentUser()?.claims?.tenant_id ?? null);




  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initAuthListener();
    }

    effect(()=>{
      this.currentUser();
    })

  }

  private initAuthListener(): void {
    onIdTokenChanged(this.auth, async (firebaseUser: User | null) => {
      if (!firebaseUser) {
        this.currentUser.set(null);
        this.isReady.set(true);
        this.broadcast({ type: 'LOGOUT' });
        return;
      }

      // On app restart — user already has claims from previous session
      // On silent token refresh — Firebase refreshed the token, rebuild user
      // On first login — claims will be empty here, facade handles this case
      const authUser = await this.buildAuthUser(firebaseUser);
      this.currentUser.set(authUser);
      this.isReady.set(true);
      this.broadcast({ type: 'TOKEN_UPDATE', user: authUser });
    });
  }

  async onBackendVerified(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return;

    // Force refresh — bypasses cache, gets new JWT with custom claims
    // from Firebase. This is necessary because the token cached in
    // memory still has the OLD claims (empty on first login).
    await firebaseUser.getIdToken(true);

    // getIdToken(true) triggers onIdTokenChanged automatically
    // which calls buildAuthUser and updates all signals.
    // But we also rebuild here immediately so facade can await
    // completion and be sure signals are ready before navigating.
    const authUser = await this.buildAuthUser(firebaseUser);
    // console.log(authUser);
    // console.log(authUser.claims.role);
    
    
    this.currentUser.set(authUser);
    this.isReady.set(true);
    this.broadcast({ type: 'TOKEN_UPDATE', user: authUser });
  }



  

  async login(provider: TenantProvider, tenantId: string): Promise<UserCredential> {
    this.auth.tenantId = tenantId;

    try {
      return await this.injector.runInContext(async () => {
        switch (provider.type) {
          case 'Google':
            return signInWithPopup(this.auth, new GoogleAuthProvider());
          case 'saml':
          case 'oidc':
            return signInWithPopup(this.auth, new OAuthProvider(provider.providerId!));
          default:
            return signInWithPopup(this.auth, new GoogleAuthProvider());
        }
      });
    } catch (error) {
      this.handleLoginError(error);
      throw error;
    }
  }





  
  async logout(): Promise<void> {
    await this.auth.signOut();
    window.location.href=window.location.origin

  }

  async forceClaimsRefresh(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return;

    await firebaseUser.getIdToken(true);
    const authUser = await this.buildAuthUser(firebaseUser);
    this.currentUser.set(authUser);
    this.broadcast({ type: 'TOKEN_UPDATE', user: authUser });
  }

  private async buildAuthUser(firebaseUser: User): Promise<AuthUser> {
    const result      = await firebaseUser.getIdTokenResult();
    const displayName = firebaseUser.displayName ?? '';
    const nameParts   = displayName.trim().split(/\s+/);
    const firstName   = nameParts[0] ?? '';
    const lastName    = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    return {
      uid:      firebaseUser.uid,
      email:    firebaseUser.email,
      firstName,
      lastName,
      fullName: displayName,
      photoURL: firebaseUser.photoURL,
      idToken:  result.token,
      claims:   result.claims as UserClaims,
    };
  }


  // ── Role helpers ──────────────────────────────────────────────

  hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(r => this.hasRole(r));
  }

  // ── Error handling ────────────────────────────────────────────

  private handleLoginError(error: unknown): void {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          break;
        case 'auth/popup-blocked':
          this.popup.error('Popup was blocked. Please allow popups.','');
          break;
        case 'auth/account-exists-with-different-credential':
          this.popup.error('Account exists with a different sign-in method.','');
          break;
        case 'auth/network-request-failed':
          this.popup.error('Network error. Please check your connection.','');
          break;
        default:
          this.popup.error('Sign-in failed. Please try again.','');
      }
    }
  }

  // ── Broadcast to MFE remotes ──────────────────────────────────

  private broadcast(payload: BroadcastPayload): void {
    this.channel.postMessage(payload);
  }


}
